import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAccessToken } from '@app/auth';
import { fetchApi } from '@app/hooks/api';
import { formatDate, formatFullDateTime } from '@app/MergeQueue/utils';
import './DeployTelapp.css';

// ── Types ────────────────────────────────────────────────────────────────────

interface DeployTelappPR {
  number: number;
  title: string;
  body?: string;
  url: string;
  chg_ticket?: string;
  merged_at: string;
  merge_commit_sha: string;
  author?: string;
  hotfix_tag?: string;
  release_tag?: string;
  deploy_repo_hash?: string;
  deploy_repo_hash_derived?: boolean;
}

interface DeployTelappTag {
  name: string;
  created_at: string;
  commit_sha: string;
  is_hotfix?: boolean;
  hotfix_fetch_failed?: boolean;
}

interface DeployTelappData {
  prs: DeployTelappPR[];
  tags: DeployTelappTag[];
}

interface DeployRepoItem {
  sha: string;
  message: string;
  committed_at: string;
  author: string;
  pr_number?: number;
  pr_title?: string;
  pr_url?: string;
  pr_merged_at?: string;
  fetch_failed?: boolean;
  pr_lookup_failed?: boolean;
}

interface TagGroup {
  tagName: string;
  tagCreatedAt?: string;
  rows: DeployTelappPR[];
  isHotfix: boolean;
  isUntagged?: boolean;
  hotfixFetchFailed?: boolean;
}

const DERIVED_HASH_TOOLTIP =
  'We have not tested this PR together with deploy repo changes in MQ. ' +
  'This is because it was a either a spec-only change, a force merge or a hotfix. ' +
  'The value here is the deploy repo hash tested with previous telapp PR';

const ORG = 'Teladoc';
const REPO = 'telapp';
// Mirrors MAX_HASHES in pages/api/github/deploytelappRepoItems.ts — the server rejects
// any single request over this count, so oversized hash sets must be split client-side.
const REPO_ITEMS_BATCH_SIZE = 50;
// Deployment tag cuts via MQ began on this date — never display data older than this.
const HARD_START = '2026-04-23T00:00:00.000Z';
const HARD_START_DAY = HARD_START.slice(0, 10);

type DatePreset = '30' | '60' | '90' | 'all';
const CHG_URL = 'https://teladochealth.atlassian.net/browse/';
const GH_PR_URL = `https://github.com/${ORG}/${REPO}/pull/`;

const formatMergedAt = (iso: string): string =>
  new Date(iso).toLocaleString('en-US', {
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit',
    hour12: false, timeZone: 'America/New_York',
  }).replace(',', '') + ' ET';

const shortSha = (sha: string): string => sha.slice(0, 10) || '—';

const relativeTime = (iso: string): string => {
  const diff = Date.now() - new Date(iso).getTime();
  const hours = Math.floor(diff / 3_600_000);
  const days  = Math.floor(diff / 86_400_000);
  if (days  > 0) { const h = hours - days * 24; return h > 0 ? `${days}d ${h}h ago` : `${days}d ago`; }
  if (hours > 0) return `${hours}h ago`;
  const mins = Math.floor(diff / 60_000);
  return mins > 0 ? `${mins}m ago` : 'just now';
};

// ── Grouping helper ──────────────────────────────────────────────────────────
/**
 * Build one group per tag since HARD_START.  Every tag appears even with zero PRs.
 *
 * Hotfix vs. regular assignment — both are exact-commit-association, never a
 * timestamp guess:
 *  - Hotfix PRs carry a `hotfix_tag` field, assigned directly to that group.
 *  - Regular PRs carry a `release_tag` field, populated backend-side by walking the
 *    actual commit ancestry between consecutive tags via the GitHub compare API.
 *    A PR with no `release_tag` hasn't been picked up by any tag yet (or its tag
 *    fell outside the compare walk) and lands in "untagged".
 */
function buildTagGroups(
  prs: DeployTelappPR[],
  tags: DeployTelappTag[],
  tagFilter: string,
): TagGroup[] {
  const hardStartTs = new Date(HARD_START).getTime();

  // All qualifying tags, oldest → newest
  const sortedTags = [...tags]
    .filter(t => new Date(t.created_at).getTime() >= hardStartTs)
    .sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());

  // Seed ALL qualifying tags as empty groups
  const map = new Map<string, TagGroup>();
  for (const tag of sortedTags) {
    map.set(tag.name, {
      tagName: tag.name,
      tagCreatedAt: tag.created_at,
      rows: [],
      isHotfix: tag.is_hotfix ?? false,
      hotfixFetchFailed: tag.hotfix_fetch_failed ?? false,
    });
  }

  // Collect PRs merged after the newest regular tag (not yet in any deployment window)
  const untaggedPRs: DeployTelappPR[] = [];

  // Assign PRs to their groups
  for (const pr of prs) {
    // Hotfix PRs: the backend already assigned them explicitly
    if (pr.hotfix_tag) {
      const group = map.get(pr.hotfix_tag);
      if (group) group.rows.push(pr);
      continue;
    }
    const mergedTs = new Date(pr.merged_at).getTime();
    if (mergedTs < hardStartTs) continue;
    // Regular PRs: exact tag assignment from the backend's commit-ancestry walk
    const group = pr.release_tag ? map.get(pr.release_tag) : undefined;
    if (!group) {
      // No tag's commit range contains this PR yet — not deployed, or its tag fell
      // outside the compare walk (e.g. missing predecessor at the window boundary).
      untaggedPRs.push(pr);
      continue;
    }
    group.rows.push(pr);
  }

  // Within each group sort PRs by merged_at desc
  let groups = Array.from(map.values());
  groups.forEach(g => g.rows.sort(
    (a, b) => new Date(b.merged_at).getTime() - new Date(a.merged_at).getTime(),
  ));

  // Apply git-tag name filter
  if (tagFilter) {
    groups = groups.filter(g => g.tagName.toLowerCase().includes(tagFilter));
  }

  // Sort tagged groups newest → oldest by tag creation date
  groups.sort((a, b) => {
    const aTs = a.tagCreatedAt ? new Date(a.tagCreatedAt).getTime() : 0;
    const bTs = b.tagCreatedAt ? new Date(b.tagCreatedAt).getTime() : 0;
    return bTs - aTs;
  });

  // Prepend the "untagged" group (always at the top — most recent, not yet released).
  // Only show when no tag filter is active or when the filter matches "untagged".
  const untaggedPassesFilter = !tagFilter || 'untagged'.includes(tagFilter);
  if (untaggedPRs.length > 0 && untaggedPassesFilter) {
    untaggedPRs.sort((a, b) => new Date(b.merged_at).getTime() - new Date(a.merged_at).getTime());
    groups.unshift({
      tagName: '__untagged__',
      rows: untaggedPRs,
      isHotfix: false,
      isUntagged: true,
    });
  }

  return groups;
}

// ── Helpers ──────────────────────────────────────────────────────────────────

/** Returns unique deploy_repo_hash values from a group's PRs in chronological order (oldest→latest). */
function getUniqueDeployHashes(prs: DeployTelappPR[]): string[] {
  const sorted = [...prs].sort(
    (a, b) => new Date(a.merged_at).getTime() - new Date(b.merged_at).getTime(),
  );
  const seen = new Set<string>();
  const hashes: string[] = [];
  for (const pr of sorted) {
    if (pr.deploy_repo_hash && !seen.has(pr.deploy_repo_hash)) {
      seen.add(pr.deploy_repo_hash);
      hashes.push(pr.deploy_repo_hash);
    }
  }
  return hashes;
}

// ── Detail row (shared by Telapp and Deploy Repo tabs) ────────────────────────

function DetailRow({ pr, isCandidate }: { pr: DeployTelappPR; isCandidate?: boolean }) {
  const ctProps = isCandidate ? {
    'data-tooltip': 'Deployment Candidate',
    onMouseMove: (e: React.MouseEvent<HTMLElement>) => {
      e.currentTarget.style.setProperty('--tip-x', `${e.clientX + 12}px`);
      e.currentTarget.style.setProperty('--tip-y', `${e.clientY + 16}px`);
    },
  } : {};
  const ctClass = isCandidate ? ' candidate-tip-trigger' : '';

  return (
    <div className={`detail-item${isCandidate ? ' detail-item--candidate' : ''}`}>
      <div className={`detail-item-left${ctClass}`} {...ctProps}>
        {pr.chg_ticket ? (
          <a
            href={`${CHG_URL}${pr.chg_ticket}`}
            target="_blank"
            rel="noopener noreferrer"
            className="detail-chg-badge"
          >
            {pr.chg_ticket}
          </a>
        ) : (
          <span className="detail-chg-badge detail-chg-badge--none">—</span>
        )}
      </div>
      <div className={`detail-item-title${ctClass}`} {...ctProps}>
        <a
          href={`${GH_PR_URL}${pr.number}`}
          target="_blank"
          rel="noopener noreferrer"
          className="detail-title-link"
          title={pr.title}
        >
          {pr.title}<span className="detail-pr-num"> · #{pr.number}</span>
        </a>
      </div>
      <span className={`detail-author${ctClass}`} {...ctProps}>{pr.author ?? '—'}</span>
      <span
        className="detail-merged-at timestamp-rel"
        data-full={formatMergedAt(pr.merged_at)}
        onMouseMove={e => {
          e.currentTarget.style.setProperty('--tip-x', `${e.clientX + 12}px`);
          e.currentTarget.style.setProperty('--tip-y', `${e.clientY + 16}px`);
        }}
      >
        {relativeTime(pr.merged_at)}
      </span>
      <code className="detail-commit">{shortSha(pr.merge_commit_sha)}</code>
      {pr.deploy_repo_hash ? (
        <code
          className={`detail-deploy-hash${pr.deploy_repo_hash_derived ? ' detail-deploy-hash--derived' : ''}`}
          {...(pr.deploy_repo_hash_derived ? {
            'data-tooltip': DERIVED_HASH_TOOLTIP,
            onMouseMove: (e: React.MouseEvent<HTMLElement>) => {
              e.currentTarget.style.setProperty('--tip-x', `${e.clientX - 12}px`);
              e.currentTarget.style.setProperty('--tip-y', `${e.clientY + 16}px`);
            },
          } : {})}
        >
          {pr.deploy_repo_hash.slice(0, 10)}{pr.deploy_repo_hash_derived && (
            <span className="derived-indicator">⚠️</span>
          )}
        </code>
      ) : (
        <code className="detail-deploy-hash">—</code>
      )}
    </div>
  );
}

// ── Component ────────────────────────────────────────────────────────────────

export const DeployTelapp: React.FC = () => {
  const getToken = useAccessToken();
  const [data, setData] = useState<DeployTelappData>({ prs: [], tags: [] });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [chgInput, setChgInput] = useState('');
  const [prInput, setPrInput] = useState('');
  const [gitTagInput, setGitTagInput] = useState('');

  const [datePreset, setDatePreset] = useState<DatePreset>('30');

  // Start all groups collapsed; expand on click
  const [expandedTags, setExpandedTags] = useState<Record<string, boolean>>({});
  const [activeTabByGroup, setActiveTabByGroup] = useState<Record<string, 'telapp' | 'deploy'>>({});
  const [selectedHashByGroup, setSelectedHashByGroup] = useState<Record<string, string>>({});

  // Deploy Repo tab: cache keyed by tagName → { [sha]: DeployRepoItem }
  const [deployItemsCache, setDeployItemsCache] = useState<Record<string, Record<string, DeployRepoItem>>>({});
  const [deployItemsLoading, setDeployItemsLoading] = useState<Record<string, boolean>>({});
  const [deployItemsError, setDeployItemsError] = useState<Record<string, boolean>>({});

  const fetchDeployItems = useCallback(async (tagName: string, hashes: string[]) => {
    // The tag's PR list (and so its hash set) can grow after the first fetch — only
    // hashes not already in the cache need to be requested. A prior total-request
    // failure leaves the cache entry unset, so every hash is "missing" again, which
    // is what lets the Retry button re-fetch.
    const cached = deployItemsCache[tagName];
    const missing = hashes.filter(h => !cached?.[h]);
    if (missing.length === 0 || deployItemsLoading[tagName]) return;
    setDeployItemsLoading(prev => ({ ...prev, [tagName]: true }));
    setDeployItemsError(prev => ({ ...prev, [tagName]: false }));
    try {
      // The server caps a single request to REPO_ITEMS_BATCH_SIZE hashes — split larger
      // groups into batches so a group over the cap can still load, instead of every
      // request 400-ing forever (including on Retry).
      const batches: string[][] = [];
      for (let i = 0; i < missing.length; i += REPO_ITEMS_BATCH_SIZE) {
        batches.push(missing.slice(i, i + REPO_ITEMS_BATCH_SIZE));
      }
      const settled = await Promise.allSettled(batches.map(batch =>
        fetchApi<Record<string, DeployRepoItem>>(
          `/api/deploy/repo-items?organization=${ORG}&hashes=${batch.join(',')}`,
          getToken,
        ),
      ));
      const merged: Record<string, DeployRepoItem> = {};
      for (const r of settled) {
        if (r.status === 'fulfilled') Object.assign(merged, r.value);
      }
      setDeployItemsCache(prev => ({ ...prev, [tagName]: { ...prev[tagName], ...merged } }));
      // Record the failure so the UI can distinguish "fetch failed" from "genuinely no metadata".
      // A failed batch leaves its hashes out of the cache, so Retry only re-requests those.
      if (settled.some(r => r.status === 'rejected')) {
        setDeployItemsError(prev => ({ ...prev, [tagName]: true }));
      }
    } catch {
      setDeployItemsError(prev => ({ ...prev, [tagName]: true }));
    } finally {
      setDeployItemsLoading(prev => ({ ...prev, [tagName]: false }));
    }
  }, [getToken, deployItemsCache, deployItemsLoading]);

  const load = useCallback(async (days: number) => {
    setLoading(true);
    setError(null);
    try {
      const result = await fetchApi<DeployTelappData>(
        `/api/deploy/telapp?organization=${ORG}&repo=${REPO}&days=${days}`,
        getToken,
      );
      setData(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load Telapp deploy data');
    } finally {
      setLoading(false);
    }
  }, [getToken]);

  useEffect(() => { load(30); }, [load]);

  const normChg = chgInput.trim().toLowerCase();
  const normPr  = prInput.trim();
  const normTag = gitTagInput.trim().toLowerCase();

  // Filter PRs by CHG and PR-number; tag filter is applied at the group level
  const filteredPRs = useMemo(
    () => data.prs.filter(pr => {
      if (normPr  && !String(pr.number).includes(normPr)) return false;
      if (normChg && !(pr.chg_ticket || '').toLowerCase().includes(normChg)) return false;
      return true;
    }),
    [data.prs, normChg, normPr],
  );

  const groups = useMemo(
    () => buildTagGroups(filteredPRs, data.tags, normTag),
    [filteredPRs, data.tags, normTag],
  );

  // Unique CHG tickets, not PR rows — a ticket that ships via a hotfix and later gets
  // merged into master (or a PR referencing more than one ticket) otherwise produces
  // more than one row for the same ticket, double-counting it.
  const uniqueChgCount = useMemo(
    () => new Set(filteredPRs.map(pr => pr.chg_ticket).filter(Boolean)).size,
    [filteredPRs],
  );
  const tagCount = groups.filter(g => !g.isUntagged).length;
  const untaggedGroup = groups.find(g => g.isUntagged);

  const expandableGroups = groups.filter(g => g.rows.length > 0);
  const allExpanded = expandableGroups.length > 0 &&
    expandableGroups.every(g => !!expandedTags[g.tagName]);

  const toggleGroup = (tagName: string) =>
    setExpandedTags(prev => ({ ...prev, [tagName]: !prev[tagName] }));

  const toggleAll = () =>
    setExpandedTags(allExpanded
      ? {}
      : Object.fromEntries(expandableGroups.map(g => [g.tagName, true])));

  // Converts a date into a day count for the API's `days` param.
  const daysSince = (dateStr: string): number =>
    Math.max(1, Math.ceil((Date.now() - new Date(`${dateStr}T00:00:00.000Z`).getTime()) / 86_400_000));

  return (
    <div className="deploy-telapp-page">
      {/* Header */}
      <div className="deploy-telapp-header">
        <h1>🚢 Deployments - Telapp <span className="header-repo">· Teladoc / telapp</span></h1>
      </div>

      {/* Filters */}
      <div className="deploy-telapp-search">
        <div className="search-field">
          <label className="search-label">CHG Ticket</label>
          <input
            className="search-input"
            placeholder="e.g. CHG-19976"
            value={chgInput}
            onChange={e => setChgInput(e.target.value)}
          />
        </div>
        <div className="search-field">
          <label className="search-label">Git Tag</label>
          <input
            className="search-input"
            placeholder="e.g. v6.8.1"
            value={gitTagInput}
            onChange={e => setGitTagInput(e.target.value)}
          />
        </div>
        <div className="search-field">
          <label className="search-label">PR Number</label>
          <input
            className="search-input"
            placeholder="e.g. 106372"
            value={prInput}
            onChange={e => setPrInput(e.target.value)}
          />
        </div>
        <div className="search-field">
          <label className="search-label">Date Range</label>
          <select
            className="search-select"
            value={datePreset}
            disabled={loading}
            onChange={e => {
              const value = e.target.value as DatePreset;
              setDatePreset(value);
              load(value === 'all' ? daysSince(HARD_START_DAY) : parseInt(value, 10));
            }}
          >
            <option value="30">30 Days ago</option>
            <option value="60">60 Days ago</option>
            <option value="90">90 Days ago</option>
            <option value="all">Beginning of Time</option>
          </select>
        </div>
      </div>

      {/* Summary bar */}
      <div className="deploy-telapp-summary">
        {loading
          ? 'Loading…'
          : [
              `${tagCount} tag${tagCount !== 1 ? 's' : ''}`,
              untaggedGroup ? `${untaggedGroup.rows.length} untagged` : null,
              `${uniqueChgCount} CHG Ticket${uniqueChgCount !== 1 ? 's' : ''}`,
            ].filter(Boolean).join(' · ')}
      </div>

      {error && <div className="deploy-telapp-error">❌ {error}</div>}

      {/* Outer grid */}
      <div className="deploy-telapp-grid">
        <table className="deploy-telapp-table">
          <thead>
            <tr>
              <th className="col-expand">
                {expandableGroups.length > 0 && (
                  <button
                    className="expand-all-btn"
                    onClick={toggleAll}
                    title={allExpanded ? 'Collapse all' : 'Expand all'}
                  >
                    {allExpanded ? '▾' : '▸'}
                  </button>
                )}
              </th>
              <th className="col-tag">Git Tag</th>
              <th className="col-created">Tag Created On</th>
              <th className="col-count">PRs</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={4} className="empty-row">Loading deploy tag data…</td>
              </tr>
            ) : groups.length === 0 ? (
              <tr>
                <td colSpan={4} className="empty-row">No results match the current filters.</td>
              </tr>
            ) : (
              groups.map(group => {
                const expanded = !!expandedTags[group.tagName];
                const hasRows = group.rows.length > 0;
                const activeTab = activeTabByGroup[group.tagName] ?? 'telapp';
                const uniqueHashes = getUniqueDeployHashes(group.rows); // oldest → latest, by telapp merge time
                const groupDeployItems = deployItemsCache[group.tagName] ?? {};
                const isDeployLoading = !!deployItemsLoading[group.tagName];
                const isDeployError = !!deployItemsError[group.tagName];

                // Once deploy data is loaded, re-sort by actual deploy repo commit date.
                // Before that, fall back to telapp merge order.
                const hasDeployData = Object.keys(groupDeployItems).length > 0;
                const orderedHashes = hasDeployData
                  ? [...uniqueHashes].sort((a, b) => {
                      const aTs = new Date(groupDeployItems[a]?.committed_at ?? 0).getTime();
                      const bTs = new Date(groupDeployItems[b]?.committed_at ?? 0).getTime();
                      return aTs - bTs; // oldest → latest
                    })
                  : uniqueHashes;

                const selectedHash = selectedHashByGroup[group.tagName] ?? orderedHashes[orderedHashes.length - 1] ?? '';
                const selectedIdx = orderedHashes.indexOf(selectedHash);
                // All hashes from oldest up to and including the selected one
                const includedHashes = orderedHashes.slice(0, selectedIdx + 1);
                return (
                  <React.Fragment key={group.tagName}>
                    {/* Group header row */}
                    <tr
                      className={`group-row${hasRows ? '' : ' group-row--empty'}${group.isUntagged ? ' group-row--untagged' : ''}${expanded ? ' group-row--expanded' : ''}`}
                      onClick={hasRows ? () => toggleGroup(group.tagName) : undefined}
                      title={hasRows ? (expanded ? 'Collapse' : 'Expand') : undefined}
                    >
                      <td className="col-expand">{hasRows ? (expanded ? '▾' : '▸') : ''}</td>
                      <td className="col-tag">
                        {group.isUntagged ? (
                          <span className="tag-chip tag-chip--untagged">untagged</span>
                        ) : (
                          <>
                            <span className="tag-chip">{group.tagName}</span>
                            {group.isHotfix && (
                              <span className="tag-chip tag-chip--hotfix">hotfix</span>
                            )}
                            {group.hotfixFetchFailed && (
                              <span
                                className="tag-chip tag-chip--fetch-failed"
                                title="Failed to fetch hotfix PRs for this tag — the PR list may be incomplete."
                              >
                                fetch failed
                              </span>
                            )}
                          </>
                        )}
                      </td>
                      <td className="col-created">
                        {group.tagCreatedAt ? (
                          <span
                            className="timestamp-rel"
                            data-full={formatFullDateTime(group.tagCreatedAt)}
                            onMouseMove={e => {
                              e.currentTarget.style.setProperty('--tip-x', `${e.clientX + 12}px`);
                              e.currentTarget.style.setProperty('--tip-y', `${e.clientY + 16}px`);
                            }}
                          >
                            {formatDate(group.tagCreatedAt)}
                          </span>
                        ) : '—'}
                      </td>
                      <td className="col-count">
                        {group.hotfixFetchFailed ? (
                          <span
                            className="pr-count-badge pr-count-badge--error"
                            title="PR count may be incomplete — hotfix PR fetch failed for this tag."
                          >
                            ⚠
                          </span>
                        ) : (
                          <span className="pr-count-badge">{group.rows.length}</span>
                        )}
                      </td>
                    </tr>

                    {/* Expanded: Telapp | Deploy Repo tab panel */}
                    {expanded && hasRows && (
                      <tr className="group-detail-row">
                        <td className="group-detail-cell" colSpan={4}>
                          <div className="detail-list">

                            {/* Tab bar */}
                            <div className="detail-tabs">
                              <button
                                className={`detail-tab-btn${activeTab === 'telapp' ? ' detail-tab-btn--active' : ''}`}
                                onClick={() => setActiveTabByGroup(prev => ({ ...prev, [group.tagName]: 'telapp' }))}
                              >
                                Telapp
                              </button>
                              <button
                                className={`detail-tab-btn${activeTab === 'deploy' ? ' detail-tab-btn--active' : ''}`}
                                onClick={() => {
                                  setActiveTabByGroup(prev => ({ ...prev, [group.tagName]: 'deploy' }));
                                  fetchDeployItems(group.tagName, uniqueHashes);
                                }}
                              >
                                Deploy Repo
                              </button>
                            </div>

                            {/* Telapp tab */}
                            {activeTab === 'telapp' && (
                              <>
                                <div className="detail-header">
                                  <span className="detail-col-label">CHG Ticket</span>
                                  <span className="detail-col-label">PR</span>
                                  <span className="detail-col-label">Author</span>
                                  <span className="detail-col-label">Merged On</span>
                                  <span className="detail-col-label">Telapp Hash</span>
                                  <span className="detail-col-label">Deploy Hash</span>
                                </div>
                                {group.rows.map((pr, idx) => (
                                  <DetailRow key={`${pr.number}:${pr.chg_ticket}`} pr={pr} isCandidate={idx === 0} />
                                ))}
                              </>
                            )}

                            {/* Deploy Repo tab */}
                            {activeTab === 'deploy' && (
                              <>
                                <div className="deploy-tab-controls">
                                  <label className="deploy-hash-label">Deploy Repo Hash</label>
                                  <select
                                    className="deploy-hash-select"
                                    value={selectedHash}
                                    onChange={e => {
                                      setSelectedHashByGroup(prev => ({ ...prev, [group.tagName]: e.target.value }));
                                      fetchDeployItems(group.tagName, uniqueHashes);
                                    }}
                                  >
                                    {[...orderedHashes].reverse().map(h => (
                                      <option key={h} value={h}>{h.slice(0, 10)}</option>
                                    ))}
                                  </select>
                                </div>
                                <div className="detail-header deploy-repo-header">
                                  <span className="detail-col-label">PR</span>
                                  <span className="detail-col-label">Author</span>
                                  <span className="detail-col-label">Merged On</span>
                                  <span className="detail-col-label">Deploy Hash</span>
                                </div>
                                {isDeployLoading ? (
                                  <div className="detail-empty">Loading deploy repo data…</div>
                                ) : isDeployError ? (
                                  <div className="detail-empty detail-empty--error">
                                    ❌ Failed to load deploy repo data.{' '}
                                    <button
                                      className="detail-retry-btn"
                                      onClick={() => fetchDeployItems(group.tagName, uniqueHashes)}
                                    >
                                      Retry
                                    </button>
                                  </div>
                                ) : includedHashes.length === 0 ? (
                                  <div className="detail-empty">No deploy hashes found for this tag.</div>
                                ) : (
                                  // includedHashes is oldest→latest; reverse for newest-first display
                                  [...includedHashes].reverse().map(hash => {
                                    const item = groupDeployItems[hash];
                                    const commitUrl = `https://github.com/${ORG}/deploy/commit/${hash}`;
                                    if (item?.fetch_failed) {
                                      return (
                                        <div key={hash} className="detail-item deploy-repo-item detail-item--fetch-failed">
                                          <div className="detail-item-title">
                                            <span className="detail-fetch-failed-label">⚠ Failed to fetch commit metadata</span>
                                          </div>
                                          <span className="detail-author">—</span>
                                          <span className="detail-merged-at">—</span>
                                          <code className="detail-deploy-hash">
                                            <a href={commitUrl} target="_blank" rel="noopener noreferrer" className="link-sha">
                                              {hash.slice(0, 10)}
                                            </a>
                                          </code>
                                        </div>
                                      );
                                    }
                                    return (
                                      <div key={hash} className="detail-item deploy-repo-item">
                                        <div className="detail-item-title">
                                          {item ? (
                                            item.pr_url ? (
                                              <a href={item.pr_url} target="_blank" rel="noopener noreferrer" className="detail-title-link" title={item.pr_title}>
                                                {item.pr_title ?? item.message}
                                                {item.pr_number && <span className="detail-pr-num"> · #{item.pr_number}</span>}
                                              </a>
                                            ) : (
                                              <div className="detail-title-with-flag">
                                                <a href={commitUrl} target="_blank" rel="noopener noreferrer" className="detail-title-link" title={item.message}>
                                                  {item.message}
                                                </a>
                                                {item.pr_lookup_failed && (
                                                  <span
                                                    className="detail-pr-lookup-failed"
                                                    title="Failed to look up the associated PR for this commit — it may have one."
                                                  >
                                                    ⚠ PR lookup failed
                                                  </span>
                                                )}
                                              </div>
                                            )
                                          ) : (
                                            <span className="text-muted">—</span>
                                          )}
                                        </div>
                                        <span className="detail-author">{item?.author ?? '—'}</span>
                                        <span
                                          className="detail-merged-at timestamp-rel"
                                          data-full={item ? formatMergedAt(item.committed_at) : ''}
                                          onMouseMove={e => {
                                            e.currentTarget.style.setProperty('--tip-x', `${e.clientX + 12}px`);
                                            e.currentTarget.style.setProperty('--tip-y', `${e.clientY + 16}px`);
                                          }}
                                        >
                                          {item ? relativeTime(item.committed_at) : '—'}
                                        </span>
                                        <code className="detail-deploy-hash">
                                          <a href={commitUrl} target="_blank" rel="noopener noreferrer" className="link-sha">
                                            {hash.slice(0, 10)}
                                          </a>
                                        </code>
                                      </div>
                                    );
                                  })
                                )}
                              </>
                            )}

                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};
